import { Box, Divider, Typography } from '@mui/material';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getActiveTicket } from 'store/ticket-service/selector';

import { NamespacesList } from 'core/enums/common';

import { CustomSwitch } from 'components/shared/CustomSwitch';

import { regularTextColor, titleTextColor } from 'assets/styles/_palette';

import { IFormattedNotification } from './index';

interface NotificationBlockProps extends IFormattedNotification {
	index: number;
	onPermissionChange?: (event: string, index: number) => void;
	isReport?: boolean;
}

export const NotificationBlock: FC<NotificationBlockProps> = ({
	title,
	description,
	permissions,
	onPermissionChange,
	index,
	isReport,
}) => {
	const { t: notificationsTranslate, i18n } = useTranslation(NamespacesList['notificationsStep']);

	const activeTicket = useSelector(getActiveTicket);

	return (
		<Box
			data-testid="notification"
			sx={{
				width: { xs: '100%', md: 'unset' },
				marginTop: { xs: '16px', md: '0' },
				flex: isReport && window.innerWidth >= 900 ? '0 0 calc(50% - 12px)' : 'none',
			}}
		>
			<Box
				className="rtl-able"
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					gap: '12px',
					marginBottom: { xs: '12px', xl: '24px' },
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between',
					}}
				>
					<Typography className="rtl-able" sx={{ fontWeight: '700', color: titleTextColor }}>
						{title}
					</Typography>
					<Typography sx={{ color: regularTextColor }} className="rtl-able">
						{description}
					</Typography>
				</Box>

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						gap: { xs: '10px', xl: '16px' },
					}}
				>
					{Object.entries(permissions).map(
						([permission, value]: [string, boolean], permissionIndex) => (
							<Box
								className="rtl-able"
								key={`${index * 3 + permissionIndex}${permission}`}
								sx={{
									width: '100%',
									display: 'flex',
									gap: '8px',
									marginRight: '5px',
									flexDirection: i18n?.language === 'ar' ? 'row-reverse' : 'row',
									justifyContent: 'space-between',
								}}
							>
								<CustomSwitch
									id={`${index * 3 + permissionIndex}${permission}`}
									checked={value}
									onChange={() => onPermissionChange && onPermissionChange(permission, index)}
								/>
								<Typography
									className="rtl-able"
									sx={{
										fontSize: '14px',
										fontWeight: '700',
										whiteSpace: 'nowrap',
										color: titleTextColor,
									}}
								>
									{notificationsTranslate(`notifications.${permission}`)}
								</Typography>
							</Box>
						)
					)}
				</Box>
			</Box>

			{activeTicket &&
				(isReport && window.innerWidth >= 900
					? activeTicket.notifications.length - 2 !== index
					: activeTicket.notifications.length - 1 !== index) &&
				activeTicket.notifications.length - 1 !== index && <Divider sx={{ marginRight: '5px' }} />}
		</Box>
	);
};
